import { Controller } from 'stimulus';
const TomSelect = require('../lib/tom_select');

// Floating popup editor for the new "cards" tasks index. One shared popup
// element appended to <body> and reused across every editable region (status,
// people, equipment, start/end dates, reminder). Sibling of
// jobs_popup_editor_controller — same lifecycle, single generic `openField`
// opener keyed by data-task-id + data-field.
//
// Because the popup lives outside this.element (so it can float with
// position: fixed without clipping), Stimulus 1.x doesn't auto-wire its
// data-action / data-target attributes; we bind them manually after each
// innerHTML injection.
export default class extends Controller {
  connect() {
    this.boundOutsideClick = this.handleOutsideClick.bind(this);
    this.boundKeydown = this.handleGlobalKey.bind(this);
    document.addEventListener('click', this.boundOutsideClick, true);
    document.addEventListener('keydown', this.boundKeydown);
    this.popupElement = document.createElement('div');
    this.popupElement.className = 'hidden fixed bg-white border rounded-lg shadow-lg';
    this.popupElement.style.zIndex = '9999';
    document.body.appendChild(this.popupElement);
    this.currentTrigger = null;
    this.activeEditor = null;
  }

  disconnect() {
    document.removeEventListener('click', this.boundOutsideClick, true);
    document.removeEventListener('keydown', this.boundKeydown);
    if (this.popupElement && this.popupElement.parentNode) {
      this.popupElement.parentNode.removeChild(this.popupElement);
    }
  }

  // Trigger carries data-task-id and data-field. The matching display element
  // (id="task-<id>-field-<field>") is swapped with the save response.
  openField(event) {
    event.preventDefault();
    const trigger = event.currentTarget;
    const taskId = trigger.dataset.taskId;
    const field = trigger.dataset.field;
    if (!taskId || !field) return;

    const query = new URLSearchParams({ field });
    // Density of the card the click came from — so the save response comes back
    // styled for that card (a compact row that's been individually expanded
    // still gets the right pill). Defaults to expanded server-side.
    if (trigger.dataset.variant) query.set('variant', trigger.dataset.variant);

    this.beginEdit(trigger, {
      formUrl: `/tasks/${taskId}/popup_field_form?${query}`,
      saveUrl: `/tasks/${taskId}/popup_field_save?${query}`,
      saveMethod: 'PATCH',
      refreshTargetId: `task-${taskId}-field-${field}`,
      prepareFormData: (popup) => {
        const fd = new FormData();
        popup.querySelectorAll('input[name], select[name], textarea[name]').forEach((el) => {
          if (el.tagName === 'SELECT' && el.multiple) {
            Array.from(el.selectedOptions).forEach((opt) => fd.append(el.name, opt.value));
          } else if (el.type === 'checkbox' || el.type === 'radio') {
            if (el.checked) fd.append(el.name, el.value);
          } else {
            fd.append(el.name, el.value);
          }
        });
        return fd;
      }
    });
  }

  // ─── Shared lifecycle ──────────────────────────────────────────────────────

  beginEdit(trigger, editorDescriptor) {
    this.close();
    this.currentTrigger = trigger;
    this.activeEditor = editorDescriptor;

    fetch(editorDescriptor.formUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(async (r) => {
        // Only a popup fragment may be injected. A redirect (sign-in page, or
        // a permission bounce from a server that still redirects) means the
        // body is a whole page, and an error status means it is a message —
        // show a notice rather than the page inside the popup.
        if (r.redirected || !r.ok) {
          this.showNotice(trigger, await this.blockedMessage(r));
          return;
        }
        const html = await r.text();
        this.popupElement.innerHTML = html;
        this.bindPopupEvents();
        this.popupElement.style.visibility = 'hidden';
        this.popupElement.classList.remove('hidden');
        this.position(trigger);
        this.popupElement.style.visibility = '';
        const first = this.popupElement.querySelector('input, select, textarea');
        if (first) first.focus();
      });
  }

  close() {
    if (!this.popupElement) return;
    if (this.tomSelectInstances) {
      this.tomSelectInstances.forEach((ts) => { try { ts.destroy(); } catch (_) { /* noop */ } });
      this.tomSelectInstances = [];
    }
    this.popupElement.classList.add('hidden');
    this.popupElement.innerHTML = '';
    const t = this.currentTrigger;
    this.currentTrigger = null;
    this.activeEditor = null;
    if (t) t.focus();
  }

  bindPopupEvents() {
    const cancelBtn = this.popupElement.querySelector('[data-popup-action="cancel"]');
    if (cancelBtn) cancelBtn.addEventListener('click', (e) => { e.preventDefault(); this.close(); });

    const saveBtn = this.popupElement.querySelector('[data-popup-action="save"]');
    if (saveBtn) saveBtn.addEventListener('click', (e) => { e.preventDefault(); this.save(); });

    this.popupElement.querySelectorAll('input, select, textarea').forEach((input) => {
      input.addEventListener('keydown', (e) => this.handleKey(e));
    });

    this.tomSelectInstances = [];
    this.popupElement.querySelectorAll('[data-popup-multi-select]').forEach((el) => {
      const ts = new TomSelect(el, {
        plugins: ['remove_button'],
        persist: false,
        maxItems: null,
        hideSelected: true,
        create: el.dataset.popupAllowCreate === 'true',
        onItemAdd: function () {
          this.setTextboxValue('');
          this.refreshOptions(false);
        }
      });
      this.tomSelectInstances.push(ts);
    });
  }

  save() {
    if (!this.activeEditor) return;
    const { saveUrl, saveMethod, refreshTargetId, prepareFormData } = this.activeEditor;
    const body = prepareFormData(this.popupElement);

    this.clearErrors();
    this.showSpinner(true);

    const csrfMeta = document.querySelector('meta[name="csrf-token"]');
    const csrf = csrfMeta ? csrfMeta.content : '';

    fetch(saveUrl, {
      method: saveMethod,
      credentials: 'same-origin',
      headers: { 'X-CSRF-Token': csrf, Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      body
    })
      .then(async (response) => {
        if (response.ok && !response.redirected) {
          const html = await response.text();
          const target = document.getElementById(refreshTargetId);
          if (target) {
            target.innerHTML = html;
            this.syncStatusVisuals(target);
          }
          this.close();
          return;
        }
        let messages = [];
        const contentType = response.headers.get('content-type') || '';
        if (response.redirected) {
          messages = ['This change could not be saved. Reload the page and try again.'];
        } else if (contentType.includes('text/plain')) {
          const text = (await response.text()).trim();
          messages = [text || `Save failed (${response.status})`];
        } else if (contentType.includes('application/json')) {
          try {
            const json = await response.json();
            messages = Array.isArray(json.errors) ? json.errors : [String(json.error || response.statusText)];
          } catch (_) {
            messages = [`Save failed (${response.status})`];
          }
        } else {
          messages = [`Save failed (${response.status})`];
        }
        this.showErrors(messages);
        this.showSpinner(false);
      })
      .catch((err) => {
        console.error('Popup save failed:', err);
        this.showErrors(['Could not reach the server. Check your connection and try again.']);
        this.showSpinner(false);
      });
  }

  // A status change recolors the card's left stripe too. The swapped-in status
  // pill carries the new color as data-stripe-color; copy it onto the stripe in
  // the same card/row (.jlv-stripe expanded · .tlv-rstripe compact). No-op for
  // other fields, whose fragments don't carry the attribute.
  // Status and start/end date edits shift status-derived bits beyond the swapped
  // fragment: the left stripe color (status only) and the timing line
  // ("Due in 3 days" → "Completed"/"late"). The fragment carries them as data-*
  // attrs; apply to the same card/row so it reflects the change without a reload.
  // No-op for other fields, whose fragments don't carry these attributes.
  syncStatusVisuals(target) {
    const source = target.querySelector('[data-timing-phrase]');
    if (!source) return;
    const card = target.closest('[name="task"]');
    if (!card) return;
    const d = source.dataset;

    // Left stripe (.jlv-stripe expanded · .tlv-rstripe compact) — status edits only.
    if (d.stripeColor) {
      const stripe = card.querySelector('.jlv-stripe, .tlv-rstripe');
      if (stripe) stripe.style.background = d.stripeColor;
    }

    // Timing line — expanded .jlv-timing (<b>phrase</b>), compact .tlv-rtiming.
    const overdue = d.timingOverdue === 'true';
    const expanded = card.querySelector('.jlv-timing');
    if (expanded) {
      expanded.classList.toggle('jlv-timing--over', overdue);
      expanded.textContent = '';
      const b = document.createElement('b');
      b.textContent = d.timingPhrase;
      expanded.appendChild(b);
    }
    const compact = card.querySelector('.tlv-rtiming');
    if (compact) {
      compact.classList.toggle('tlv-rtiming--over', overdue);
      compact.textContent = d.timingPhrase;
    }
  }

  // Message for a form fetch that did not return a popup fragment: the
  // server's plain-text reason for a refused request, otherwise a generic
  // nudge (a redirect means the page or session moved on).
  async blockedMessage(response) {
    const contentType = response.headers.get('content-type') || '';
    if (!response.redirected && !response.ok && contentType.includes('text/plain')) {
      const text = (await response.text()).trim();
      if (text) return text;
    }
    return 'This editor could not be opened. Reload the page and try again.';
  }

  // Text-only popup in place of the editor. Escape / outside click close it.
  showNotice(trigger, message) {
    this.popupElement.innerHTML = '';
    const box = document.createElement('div');
    box.className = 'p-3 text-xs text-gray-700';
    box.style.width = '260px';
    box.dataset.popupRole = 'notice';
    box.textContent = message;
    this.popupElement.appendChild(box);
    this.popupElement.classList.remove('hidden');
    this.position(trigger);
  }

  clearErrors() {
    const existing = this.popupElement.querySelector('[data-popup-role="errors"]');
    if (existing) existing.remove();
  }

  showErrors(messages) {
    this.clearErrors();
    const errEl = document.createElement('div');
    errEl.dataset.popupRole = 'errors';
    errEl.className = 'mt-2 p-2 text-xs rounded';
    errEl.style.cssText = 'background:#fee2e2;color:#991b1b;border:1px solid #fecaca;';
    errEl.innerHTML = messages.map((m) => `<div>${this.escapeHtml(m)}</div>`).join('');
    const buttons = this.popupElement.querySelector('[data-popup-action="save"]');
    const buttonsRow = buttons ? buttons.parentElement : null;
    if (buttonsRow) {
      buttonsRow.parentNode.insertBefore(errEl, buttonsRow);
    } else {
      this.popupElement.appendChild(errEl);
    }
  }

  escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  handleKey(event) {
    if (event.key === 'Enter' && event.target.tagName !== 'TEXTAREA') {
      event.preventDefault();
      this.save();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  handleGlobalKey(event) {
    if (event.key === 'Escape' && this.popupElement && !this.popupElement.classList.contains('hidden')) {
      this.close();
    }
  }

  handleOutsideClick(event) {
    if (!this.popupElement || this.popupElement.classList.contains('hidden')) return;
    if (this.popupElement.contains(event.target)) return;
    if (this.currentTrigger && this.currentTrigger.contains(event.target)) return;
    this.close();
  }

  position(trigger) {
    const rect = trigger.getBoundingClientRect();
    const popupRect = this.popupElement.getBoundingClientRect();
    const popupWidth = popupRect.width || 260;
    const popupHeight = popupRect.height || 200;
    const margin = 8;

    let left = rect.left + window.scrollX;
    if (left + popupWidth + margin > window.innerWidth + window.scrollX) {
      left = Math.max(margin, window.innerWidth + window.scrollX - popupWidth - margin);
    }

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    let top;
    if (spaceBelow >= popupHeight + 4 || spaceBelow >= spaceAbove) {
      top = rect.bottom + window.scrollY + 4;
    } else {
      top = rect.top + window.scrollY - popupHeight - 4;
      if (top < window.scrollY + margin) top = window.scrollY + margin;
    }

    this.popupElement.style.left = `${left}px`;
    this.popupElement.style.top = `${top}px`;
  }

  showSpinner(on) {
    const spinner = this.popupElement.querySelector('[data-popup-role="spinner"]');
    const text = this.popupElement.querySelector('[data-popup-role="save-text"]');
    const btn = this.popupElement.querySelector('[data-popup-action="save"]');
    if (spinner) spinner.classList.toggle('hidden', !on);
    if (text) text.textContent = on ? 'Saving…' : 'Save';
    if (btn) btn.disabled = on;
  }
}
